import { Outlet, Link, useNavigate } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Logo } from '#app/components/ui/logo.tsx'

export default function Route() {
	const navigate = useNavigate()
	return (
		<div className="flex h-screen overflow-hidden">
			{/* Left Panel - Branding */}
			<div className="relative m-2 hidden flex-col justify-between overflow-hidden rounded-2xl border border-neutral-200 bg-[#fefefe] p-12 lg:flex lg:w-1/2">
				{/* Grid background */}
				<div
					className="absolute inset-0 opacity-[0.12]"
					style={{
						backgroundImage:
							'linear-gradient(#d5d0c8 1px, transparent 1px), linear-gradient(90deg, #d5d0c8 1px, transparent 1px)',
						backgroundSize: '80px 80px',
					}}
				/>

				{/* Animated waves */}
				<div className="absolute inset-0 overflow-hidden">
					<AuthWave />
				</div>

				{/* Logo */}
				<div className="relative z-10">
					<Link to="/" className="flex items-center gap-3">
						<Logo />
						<span className="text-lg font-semibold tracking-tight text-neutral-900">IUS PRO</span>
					</Link>
				</div>

				{/* Back Link */}
				<div className="relative z-10">
					{/* Main Content */}
					<div className="relative z-10 space-y-6 pb-16">
						<h1 className="max-w-md text-balance text-4xl font-light leading-[1.1] tracking-[-0.08em] text-neutral-950 md:text-5xl">
							La Inteligencia Artificial
							<br />
							<span className="text-neutral-400">Más Precisa para</span>
							<br />
							Abogados
						</h1>
						<p className="max-w-md text-lg leading-relaxed text-neutral-500">
							Analiza expedientes, redacta documentos de alta calidad y descubre las estrategias más efectivas para
							ganar tus casos.
						</p>
						<div className="flex items-center gap-6 text-sm text-neutral-400">
							<span className="flex items-center gap-2">
								<span className="h-1 w-1 bg-neutral-950" />
								Disponible 24/7
							</span>
							<span className="flex items-center gap-2">
								<span className="h-1 w-1 bg-neutral-950" />6 consultas gratis
							</span>
						</div>
					</div>
					<Button
						onClick={() => navigate(-1)}
						variant="link"
						className="px-0 text-sm text-neutral-500 hover:text-neutral-900"
					>
						<Icon name="arrow-left" className="mr-1 h-4 w-4" />
						Volver al inicio
					</Button>
				</div>
			</div>

			{/* Right Panel - Content */}
			<div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto lg:w-1/2">
				{/* Form Container */}
				<div className="flex h-full items-center justify-center p-6 pb-16 lg:p-12">
					<Outlet />
				</div>
			</div>
		</div>
	)
}

function AuthWave() {
	const waveColors = {
		start: '#1a2744',
		mid1: '#2d3f5f',
		mid2: '#c9a962',
		mid3: '#e8d5a3',
		end: '#0f1829',
	}

	return (
		<svg
			viewBox="0 0 1400 900"
			className="absolute -left-[15%] -top-[15%] h-[130%] w-[130%]"
			preserveAspectRatio="xMidYMid slice"
		>
			<defs>
				<linearGradient id="auth-wave-grad" x1="0%" y1="100%" x2="100%" y2="0%">
					<stop offset="0%" stopColor={waveColors.start}>
						<animate
							attributeName="stop-color"
							values={`${waveColors.start};${waveColors.mid2};${waveColors.start}`}
							dur="10s"
							repeatCount="indefinite"
						/>
					</stop>
					<stop offset="50%" stopColor={waveColors.mid2}>
						<animate
							attributeName="stop-color"
							values={`${waveColors.mid2};${waveColors.mid3};${waveColors.mid2}`}
							dur="8s"
							repeatCount="indefinite"
						/>
					</stop>
					<stop offset="100%" stopColor={waveColors.end}>
						<animate
							attributeName="stop-color"
							values={`${waveColors.end};${waveColors.mid1};${waveColors.end}`}
							dur="12s"
							repeatCount="indefinite"
						/>
					</stop>
				</linearGradient>
				<filter id="auth-wave-glow" x="-50%" y="-50%" width="200%" height="200%">
					<feGaussianBlur stdDeviation="12" result="coloredBlur" />
					<feMerge>
						<feMergeNode in="coloredBlur" />
						<feMergeNode in="SourceGraphic" />
					</feMerge>
				</filter>
			</defs>

			<path
				d="M-200,700 C100,750 200,400 500,350 C800,300 900,550 1200,500 C1500,450 1600,200 1700,150"
				fill="none"
				stroke="url(#auth-wave-grad)"
				strokeWidth="120"
				strokeLinecap="round"
				filter="url(#auth-wave-glow)"
				opacity="0.85"
			>
				<animate
					attributeName="d"
					dur="18s"
					repeatCount="indefinite"
					values="
						M-200,700 C100,750 200,400 500,350 C800,300 900,550 1200,500 C1500,450 1600,200 1700,150;
						M-200,650 C100,700 200,350 500,300 C800,250 900,500 1200,450 C1500,400 1600,150 1700,100;
						M-200,720 C100,770 200,420 500,370 C800,320 900,570 1200,520 C1500,470 1600,220 1700,170;
						M-200,700 C100,750 200,400 500,350 C800,300 900,550 1200,500 C1500,450 1600,200 1700,150
					"
				/>
			</path>

			<path
				d="M-150,750 C150,800 250,450 550,400 C850,350 950,600 1250,550 C1550,500 1650,250 1750,200"
				fill="none"
				stroke={waveColors.mid3}
				strokeWidth="40"
				strokeLinecap="round"
				opacity="0.4"
			>
				<animate
					attributeName="d"
					dur="15s"
					repeatCount="indefinite"
					values="
						M-150,750 C150,800 250,450 550,400 C850,350 950,600 1250,550 C1550,500 1650,250 1750,200;
						M-150,700 C150,750 250,400 550,350 C850,300 950,550 1250,500 C1550,450 1650,200 1750,150;
						M-150,750 C150,800 250,450 550,400 C850,350 950,600 1250,550 C1550,500 1650,250 1750,200
					"
				/>
			</path>

			<path
				d="M-100,800 C200,850 300,500 600,450 C900,400 1000,650 1300,600"
				fill="none"
				stroke={waveColors.mid2}
				strokeWidth="4"
				strokeLinecap="round"
				opacity="0.5"
			/>
		</svg>
	)
}
